import {
  FullLoanDeal,
  LoanDeal,
  Property,
  Ticket,
  LoanLedgerEntry,
  Document,
  ClientCommitment,
  Borrower,
  FinancialTerms,
  LoanEvent,
  Notification,
  ClientLead,
  LoanDealVersion,
  BorrowerLedgerEntry,
  RedemptionStatement,
} from '../models/loan.model';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore'; // FIX: Consolidated imports
import { FIRESTORE } from '../firebase.config';
import { sanitizeData } from '../utils/firebase.utils';
import { UserProfile } from '../models/user.model';
import { Injectable, inject, signal, computed, effect } from '@angular/core';
// FIX: Imported AuthService to resolve injection type errors.
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private authService = inject(AuthService);
  private unsubscribers: (() => void)[] = [];

  // Firestore collection data signals - now public readonly
  readonly users = signal<UserProfile[]>([]);
  readonly loanDeals = signal<LoanDeal[]>([]);
  readonly properties = signal<Property[]>([]);
  readonly borrowers = signal<Borrower[]>([]);
  readonly financialTerms = signal<FinancialTerms[]>([]);
  readonly documents = signal<Document[]>([]);
  // private _clientCommitments will hold all commitments for admin, or confirmed for client
  private readonly _clientCommitments = signal<ClientCommitment[]>([]);
  // private _currentUserCommitments will hold the client's own commitments (pending and confirmed)
  private readonly _currentUserCommitments = signal<ClientCommitment[]>([]);

  // Public readonly computed signal that merges both commitment sources
  readonly clientCommitments = computed(() => {
    const all = [...this._clientCommitments(), ...this._currentUserCommitments()];
    const unique = new Map<string, ClientCommitment>();
    // The user's own commitments from their subcollection are the source of truth for them
    // So we add them last to overwrite any stale/unconfirmed data from the public collection
    all.forEach(c => unique.set(c.id, c));
    return Array.from(unique.values());
  });

  readonly loanEvents = signal<LoanEvent[]>([]);
  readonly loanLedger = signal<LoanLedgerEntry[]>([]); // Client-facing capital ledger
  readonly borrowerLedger = signal<BorrowerLedgerEntry[]>([]); // NEW: Borrower-facing transaction ledger
  readonly tickets = signal<Ticket[]>([]);
  readonly notifications = signal<Notification[]>([]);
  readonly clientLeads = signal<ClientLead[]>([]);
  readonly loanDealVersions = signal<LoanDealVersion[]>([]);
  readonly redemptionStatements = signal<RedemptionStatement[]>([]);

  // Intermediate computed signal to join documents to their properties
  private propertiesWithDocuments = computed(() => {
    const props = this.properties();
    const docs = this.documents();
    return props.map(property => ({
      ...property,
      documents: docs.filter(d => d.propertyId === property.id)
    }));
  });

  // Intermediate computed signal to join most related data to deals
  private dealsWithRelatedData = computed(() => {
    const deals = this.loanDeals();
    const borrows = this.borrowers();
    const terms = this.financialTerms();
    const commits = this.clientCommitments();
    const events = this.loanEvents();
    const loanLedgers = this.loanLedger();
    const borrowerLedgers = this.borrowerLedger();

    return deals.map(deal => {
      const borrower = borrows.find(b => b.loanDealId === deal.id);
      const ft = terms.find(ft => ft.loanDealId === deal.id);

      if (!borrower) {
        console.warn(`DataService: Missing borrower for deal ${deal.id} (${deal.loanDealName})`);
      }
      if (!ft) {
        console.warn(`DataService: Missing financialTerms for deal ${deal.id} (${deal.loanDealName})`);
      }

      return {
        loanDeal: deal,
        borrower: borrower!, // Still using ! as it's required in FullLoanDeal interface, but we've logged it
        financialTerms: ft!,
        commitments: commits.filter(c => c.loanDealId === deal.id),
        events: events.filter(e => e.loanDealId === deal.id),
        loanLedger: loanLedgers.filter(la => la.loanDealId === deal.id),
        borrowerLedger: borrowerLedgers.filter(bl => bl.loanDealId === deal.id),
      };
    });
  });

  // Final client-side join to construct the FullLoanDeal object
  allDeals = computed<FullLoanDeal[]>(() => {
    const dealsWithData = this.dealsWithRelatedData();
    const propsWithDocs = this.propertiesWithDocuments();
    const allDocs = this.documents();

    return dealsWithData.map(dealWithData => {
      const dealId = dealWithData.loanDeal.id;
      return {
        ...dealWithData,
        properties: propsWithDocs.filter(p => p.loanDealId === dealId),
        documents: allDocs.filter(d => d.loanDealId === dealId && !d.propertyId)
      };
    });
  });

  constructor() {
    effect(() => {
      this.cleanupSubscriptions();
      if (this.authService.isAuthenticated()) {
        this.initializeSubscriptions();
      } else {
        // Clear data on logout
        this.users.set([]);
        this.loanDeals.set([]);
        this.properties.set([]);
        this.borrowers.set([]);
        this.financialTerms.set([]);
        this.documents.set([]);
        this._clientCommitments.set([]);
        this._currentUserCommitments.set([]);
        this.loanEvents.set([]);
        this.loanLedger.set([]);
        this.borrowerLedger.set([]); // NEW: Clear on logout
        this.tickets.set([]);
        this.notifications.set([]);
        this.clientLeads.set([]);
        this.loanDealVersions.set([]);
        this.redemptionStatements.set([]);
      }
    });
  }

  private initializeSubscriptions(): void {
    const userRole = this.authService.currentUserRole();
    const user = this.authService.userProfile();

    const createSubscription = <T>(q: any, signalUpdater: (data: T[]) => void, name: string) => {
      const unsubscribe = onSnapshot(q, snapshot => {
        const data = snapshot.docs.map(doc => sanitizeData({ ...doc.data(), id: doc.id })) as T[];
        signalUpdater(data);
      }, error => console.error(`Error fetching ${name}: `, error));
      this.unsubscribers.push(unsubscribe);
    };

    // Public collections are subscribed to by everyone
    createSubscription<LoanDeal>(collection(FIRESTORE, 'loanDeals'), data => this.loanDeals.set(data), 'loanDeals');
    createSubscription<Property>(collection(FIRESTORE, 'properties'), data => this.properties.set(data), 'properties');
    createSubscription<Borrower>(collection(FIRESTORE, 'borrowers'), data => this.borrowers.set(data), 'borrowers');
    createSubscription<FinancialTerms>(collection(FIRESTORE, 'financialTerms'), data => this.financialTerms.set(data), 'financialTerms');
    createSubscription<Document>(collection(FIRESTORE, 'documents'), data => this.documents.set(data), 'documents');

    // Role-dependent collections
    if (userRole?.toLowerCase() === 'admin') {
      // Admin gets everything
      createSubscription<UserProfile>(collection(FIRESTORE, 'users'), data => this.users.set(data), 'users');
      createSubscription<ClientCommitment>(collection(FIRESTORE, 'clientCommitments'), data => this._clientCommitments.set(data), 'clientCommitments');
      this._currentUserCommitments.set([]);
      createSubscription<Ticket>(collection(FIRESTORE, 'tickets'), data => this.tickets.set(data), 'tickets');
      createSubscription<LoanEvent>(collection(FIRESTORE, 'loanEvents'), data => this.loanEvents.set(data), 'loanEvents');
      createSubscription<LoanLedgerEntry>(collection(FIRESTORE, 'loanLedger'), data => {
        console.log('DataService: Received loanLedger from Firestore:', JSON.stringify(data));
        this.loanLedger.set(data);
      }, 'loanLedger');
      createSubscription<BorrowerLedgerEntry>(collection(FIRESTORE, 'borrowerLedger'), data => this.borrowerLedger.set(data), 'borrowerLedger'); // NEW: Admin subscribes to borrower ledger
      createSubscription<ClientLead>(collection(FIRESTORE, 'clientLeads'), data => this.clientLeads.set(data), 'clientLeads');
      createSubscription<LoanDealVersion>(collection(FIRESTORE, 'loanDealVersions'), data => this.loanDealVersions.set(data), 'loanDealVersions');
      createSubscription<Notification>(collection(FIRESTORE, 'notifications'), data => this.notifications.set(data), 'notifications');
      createSubscription<RedemptionStatement>(collection(FIRESTORE, 'redemptionStatements'), data => this.redemptionStatements.set(data), 'redemptionStatements');

    } else if (user && userRole === 'Client') {
      const userDocPath = `users/${user.uid}`;

      // COMMITMENTS: Different subscription strategy for trustees vs regular clients
      if (user.subRole === 'Trustee' || user.subRole === 'ReadOnly') {
        // Trustees subscribe to ALL commitments (confirmed + pending) from top-level collection
        // This includes their own AND managed clients' commitments
        // ClientService.commitmentsForCurrentUser filters by effectiveUserId
        const allCommitmentsQuery = collection(FIRESTORE, 'clientCommitments');
        createSubscription<ClientCommitment>(allCommitmentsQuery, data => {
          // Set both signals with all data - ClientService will filter
          this._clientCommitments.set(data);
          this._currentUserCommitments.set([]);
        }, 'allCommitments');
      } else {
        // Regular clients: subscribe to confirmed commitments for deal progress...
        const confirmedCommitmentsQuery = query(collection(FIRESTORE, 'clientCommitments'), where('isConfirmed', '==', true));
        createSubscription<ClientCommitment>(confirmedCommitmentsQuery, data => this._clientCommitments.set(data), 'confirmedClientCommitments');

        // ...and their own subcollection for pending commitments
        const userCommitmentsQuery = collection(FIRESTORE, `${userDocPath}/clientCommitments`);
        createSubscription<ClientCommitment>(userCommitmentsQuery, data => this._currentUserCommitments.set(data), 'currentUserCommitments');
      }

      // TICKETS: Trustees subscribe to top-level collection, regular clients to their subcollection
      if (user.subRole === 'Trustee' || user.subRole === 'ReadOnly') {
        // Trustees see all tickets (filtered by TicketService.ticketsForCurrentUser)
        // FIXED: Use subcollection to avoid permission errors, same as regular clients
        const ticketsQuery = query(collection(FIRESTORE, `${userDocPath}/tickets`), limit(100));
        createSubscription<Ticket>(ticketsQuery, data => this.tickets.set(data), 'tickets');
      } else {
        // Regular clients see only their own tickets from subcollection
        const ticketsQuery = query(collection(FIRESTORE, `${userDocPath}/tickets`), limit(100));
        createSubscription<Ticket>(ticketsQuery, data => this.tickets.set(data), 'tickets');
      }

      const notificationsQuery = query(collection(FIRESTORE, `${userDocPath}/notifications`), orderBy('timestamp', 'desc'), limit(100));
      createSubscription<Notification>(notificationsQuery, data => this.notifications.set(data), 'notifications');

      const statementsQuery = query(collection(FIRESTORE, 'redemptionStatements'), where('userId', '==', user.uid));
      createSubscription<RedemptionStatement>(statementsQuery, data => this.redemptionStatements.set(data), 'redemptionStatements');

      // Explicitly clear admin-only data for clients to prevent stale data.
      this.users.set([]);
      this.loanEvents.set([]);
      this.loanLedger.set([]);
      this.borrowerLedger.set([]); // NEW: Clear on logout/for clients
      this.clientLeads.set([]);
      this.loanDealVersions.set([]);

    } else {
      // User has no role or an unknown role, clear all protected data.
      this.users.set([]);
      this._clientCommitments.set([]);
      this._currentUserCommitments.set([]);
      this.tickets.set([]);
      this.loanEvents.set([]);
      this.loanLedger.set([]);
      this.borrowerLedger.set([]); // NEW: Clear on logout/for unknown role
      this.notifications.set([]);
      this.clientLeads.set([]);
      this.loanDealVersions.set([]);
      this.redemptionStatements.set([]);
    }
  }

  private cleanupSubscriptions(): void {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
  }

  /**
   * Utility method to query a Firestore collection with filters
   */
  async query<T>(
    collectionName: string,
    filters: Array<{ field: string; operator: any; value: any }>,
    limitCount?: number
  ): Promise<T[]> {
    try {
      let q = collection(FIRESTORE, collectionName) as any;
      const constraints: any[] = [];

      if (filters.length > 0) {
        constraints.push(...filters.map(f => where(f.field, f.operator, f.value)));
      }

      if (limitCount) {
        const { limit } = await import('firebase/firestore');
        constraints.push(limit(limitCount));
      }

      if (constraints.length > 0) {
        q = query(q, ...constraints);
      }

      const snapshot = await import('firebase/firestore').then(m => m.getDocs(q));
      return snapshot.docs.map(doc => sanitizeData({ ...(doc.data() as any), id: doc.id }) as T);
    } catch (error) {
      console.error(`Error querying ${collectionName}:`, error);
      return [];
    }
  }

  /**
   * Utility method to add a document to a Firestore collection
   */
  async add(collectionName: string, data: any): Promise<string> {
    try {
      const { addDoc, collection: firestoreCollection } = await import('firebase/firestore');
      const ref = await addDoc(firestoreCollection(FIRESTORE, collectionName), data);
      return ref.id;
    } catch (error) {
      console.error(`Error adding document to ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Utility method to update a Firestore document
   */
  async updateDocument(path: string, data: any): Promise<void> {
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const docRef = doc(FIRESTORE, path);
      await updateDoc(docRef, data);
    } catch (error) {
      console.error(`Error updating document at ${path}:`, error);
      throw error;
    }
  }

  /**
   * Manually remove a commitment from the local cache.
   * This is used when a user (e.g. Trustee) deletes a commitment but cannot delete the top-level document.
   * We remove it locally so the UI updates immediately.
   */
  removeLocalCommitment(commitmentId: string): void {
    const current = this._clientCommitments();
    this._clientCommitments.set(current.filter(c => c.id !== commitmentId));
  }

  /**
   * Manually update a commitment in the local cache.
   * This keeps optimistic/local state aligned while Firestore listeners catch up.
   */
  updateLocalCommitment(commitmentId: string, updates: Partial<ClientCommitment>): void {
    const applyUpdates = (commitments: ClientCommitment[]) => commitments.map(c => {
      if (c.id === commitmentId) {
        return { ...c, ...updates };
      }
      return c;
    });

    this._clientCommitments.set(applyUpdates(this._clientCommitments()));
    this._currentUserCommitments.set(applyUpdates(this._currentUserCommitments()));
  }
}
